<template>
    <Breadcrumb :items="breadcrumbs" />
    <!-- Deliberately its own class, not the shared .absence-page/.absence-page-mobile
         pair (also used by absence.vue) — those hide the whole page on mobile/tablet
         and show a "desktop only" message; this page must stay usable on mobile,
         with the wide table scrolling horizontally instead (see #paymentsTable below). -->
    <div class="payments-page container-fluid my-3">

        <!-- ── QR Panel ── -->
        <div class="card mb-3 border-primary">
            <div class="card-body py-3">
                <h6 class="card-title mb-3">
                    <i class="pi pi-qrcode me-1"></i> تسجيل الدفع بـ QR
                </h6>
                <div class="row align-items-end g-2 mb-3 p-3">
                    <div class="col-auto">
                        <label class="form-label mb-1 fw-semibold">البند المالي الحالي</label>
                        <Dropdown
                            v-model="selectedQrItem"
                            :options="financialItems"
                            optionLabel="name"
                            placeholder="اختر البند المالي..."
                            class="w-100"
                            style="min-width:220px"
                        />
                    </div>
                    <div v-if="selectedQrItem" class="col-auto">
                        <span class="badge bg-success fs-6 px-3 py-2">
                            <i class="pi pi-check-circle me-1"></i>
                            {{ selectedQrItem.name }}
                        </span>
                    </div>
                    <div v-else class="col-auto">
                        <span class="badge bg-secondary fs-6 px-3 py-2">لم يتم اختيار بند</span>
                    </div>
                </div>
                <BarcodeScanner
                    v-if="selectedQrItem"
                    mode="payment"
                    @scanned="handlePaymentQR"
                />
                <p v-else class="text-muted small mb-0">اختر البند المالي أولاً لتفعيل الماسح.</p>
                <div v-if="scanLookupLoading" class="mt-2 text-primary small">
                    <span class="spinner-border spinner-border-sm me-1"></span>
                    جاري البحث عن الطالب...
                </div>
            </div>
        </div>

        <!-- ── Filters ── -->
        <div class="filter my-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
                <h4>
                    المدفوعات — {{ gradeName }}
                    <span v-if="paggination">({{ paggination }})</span>
                </h4>
            </div>
            <div class="row">
                <div class="col-lg-8 col-md-12 col-12">
                    <div class="form-group">
                        <label for="pay-search">ابحث</label>
                        <input type="search" id="pay-search" class="form-control" placeholder="ابحث باسم الطالب"
                            v-model="word" @keyup="fetchStudents(1)">
                    </div>
                </div>
                <div class="col-lg-2 col-md-6 col-12">
                    <div class="form-group d-flex flex-column">
                        <label>الجنس</label>
                        <Dropdown v-model="gender" :options="gender_list" optionLabel="title"
                            placeholder="الجنس" @change="fetchStudents(1)" />
                    </div>
                </div>
                <div class="col-lg-2 col-md-3 col-12">
                    <div class="d-flex">
                        <div class="form-group d-flex flex-column mx-1">
                            <label class="non_opacity">Excel</label>
                            <button class="btn btn-success px-3" @click="exportToExcel">
                                <i class="pi pi-file-excel"></i>
                            </button>
                        </div>
                        <div class="form-group d-flex flex-column mx-1">
                            <label class="non_opacity">طباعة</label>
                            <button class="btn btn-danger px-3" v-print="'#paymentsTable'">
                                <i class="pi pi-file-pdf"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Bulk unpaid notify ── -->
        <div class="row mb-3 mt-1" v-if="financialItems.length > 0">
            <div class="col-12">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                    <Dropdown
                        v-model="bulkNotifyItem"
                        :options="financialItems"
                        optionLabel="name"
                        placeholder="اختر البند لإشعار غير المسددين..."
                        style="min-width:250px"
                    />
                    <button class="btn btn-warning" @click="sendBulkUnpaidNotify"
                        :disabled="bulkNotifying || !bulkNotifyItem">
                        <i class="pi pi-bell me-1"></i>
                        {{ bulkNotifying ? 'جاري الإرسال...' : 'إرسال إشعار غير المسددين' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- ── No items warning ── -->
        <div v-if="!loadingItems && financialItems.length === 0" class="alert alert-warning">
            لا توجد بنود مالية لهذا الفصل.
            <router-link to="/financial-settings" class="alert-link ms-1">
                أضف بنوداً من الإعدادات المالية
            </router-link>
        </div>

        <!-- ── Main Table ── -->
        <div class="row">
            <div id="paymentsTable" class="a4">
                <div class="first-screen-pdf">
                    <h1>شيت المدفوعات</h1>
                    <h2>{{ gradeName }}</h2>
                    <h3>{{ gender?.id === 1 ? 'بنين' : gender?.id === 2 ? 'بنات' : '' }} ({{ paggination }})</h3>
                </div>

                <table class="table table-striped table-confirm">
                    <thead>
                        <tr>
                            <th scope="col" rowspan="2">رقم</th>
                            <th scope="col" rowspan="2">الاسم</th>
                            <th scope="col" rowspan="2">رقم ولي الأمر</th>
                            <th scope="col" :colspan="financialItems.length || 1">
                                البنود المالية
                            </th>
                        </tr>
                        <tr>
                            <th v-if="financialItems.length === 0" class="month text-muted">—</th>
                            <th v-for="item in financialItems" :key="item.id" class="month pay-col">
                                {{ item.name }}
                                <div v-if="item.amount" class="item-amount">{{ item.amount }} ج</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(student, index) in reservations.data" :key="student.user_id"
                            :data-student-row="student.user_id"
                            :class="{ 'row-highlight': highlightedStudentId === student.user_id }">
                            <td data-label="رقم">{{ rowNumber(index) }}</td>
                            <td data-label="الاسم">{{ student.name }}</td>
                            <td data-label="رقم ولي الأمر">{{ student.parent_phone }}</td>
                            <td v-if="financialItems.length === 0"></td>
                            <td v-for="item in financialItems" :key="item.id"
                                class="pay-cell"
                                :class="{ 'pay-cell--paid': isPaid(student.user_id, item.id) }"
                                :title="getPaidTitle(student.user_id, item.id)"
                                @click="handleCellClick(student, item)">
                                <i v-if="isPaid(student.user_id, item.id)" class="pi pi-check pay-check"></i>
                            </td>
                        </tr>

                        <!-- Extra rows for print pagination -->
                        <tr v-for="n in extraRows" :key="'empty-' + n" class="empty-row">
                            <td>{{ (reservations.data?.length ?? 0) + n }}</td>
                            <td></td>
                            <td></td>
                            <td v-if="financialItems.length === 0"></td>
                            <td v-for="item in financialItems" :key="item.id"></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ── Pagination ── -->
        <div class="row">
            <div class="col-md-10 col-12">
                <pagination :data="reservations"
                    class="mx-auto d-flex align-items-center justify-content-center pagination"
                    @pagination-change-page="fetchStudents">
                    <template v-slot:prev-nav>&lt;</template>
                    <template v-slot:next-nav>&gt;</template>
                </pagination>
            </div>
            <div class="col-md-2 col-12 mt-md-0 mt-3">
                <div class="form-group d-flex md-justify-content-end justify-content-center">
                    <Dropdown v-model="per_page" :options="per_pages" showClear optionLabel="title"
                        placeholder="عدد الطلاب" @change="fetchStudents(1)" />
                </div>
            </div>
        </div>

    </div>

    <!-- ── Payment Details Dialog ── -->
    <Dialog v-model:visible="detailsVisible" modal header="تفاصيل الدفع"
        :style="{ width: '40vw' }" :breakpoints="{ '1199px': '65vw', '575px': '90vw' }">
        <div v-if="selectedPayment">
            <table class="table table-bordered">
                <tbody>
                    <tr>
                        <th class="bg-light" style="width:40%">الطالب</th>
                        <td>{{ selectedPayment.student_name }}</td>
                    </tr>
                    <tr>
                        <th class="bg-light">البند المالي</th>
                        <td>{{ selectedPayment.item_name }}</td>
                    </tr>
                    <tr v-if="selectedPayment.amount">
                        <th class="bg-light">القيمة</th>
                        <td>{{ selectedPayment.amount }} جنيه</td>
                    </tr>
                    <tr>
                        <th class="bg-light">تاريخ الدفع</th>
                        <td>{{ formatDate(selectedPayment.paid_at) }}</td>
                    </tr>
                    <tr>
                        <th class="bg-light">وقت الدفع</th>
                        <td>{{ formatTime(selectedPayment.paid_at) }}</td>
                    </tr>
                    <tr v-if="selectedPayment.paid_by_name">
                        <th class="bg-light">سُجّل بواسطة</th>
                        <td>{{ selectedPayment.paid_by_name }}</td>
                    </tr>
                    <tr v-if="selectedPayment.notes">
                        <th class="bg-light">ملاحظات</th>
                        <td>{{ selectedPayment.notes }}</td>
                    </tr>
                </tbody>
            </table>
            <button class="btn btn-danger mt-2" @click="removePayment">
                <i class="pi pi-trash me-1"></i> إلغاء تسجيل الدفع
            </button>
        </div>
    </Dialog>

    <!-- ── Notification Dialog ── -->
    <Dialog v-model:visible="notifyVisible" modal header="رسالة تأكيد الدفع"
        :style="{ width: '50vw' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
        <Textarea v-model="paymentMessage" style="display:block;width:100%;" cols="30" rows="7" />
        <div class="text-end mt-1" :class="paymentMessage.length > 70 ? 'text-danger' : 'text-success'">
            {{ paymentMessage.length }} حرف
            <span class="ms-2">عدد الرسائل: {{ smsCount }}</span>
        </div>
        <button type="button" class="PrimaryButton mt-3" @click="sendNotification">إرسال</button>
    </Dialog>

    <!-- Unpaid-fees bulk-notify preview/confirmation -->
    <NotificationPreviewDialog
        v-model:visible="unpaidPreview.visible"
        title="معاينة إشعار غير المسددين"
        :loading="unpaidPreview.loading"
        :sending="unpaidPreview.sending"
        :whatsapp-available="unpaidPreview.whatsappAvailable"
        :channel="unpaidPreview.channel"
        :summary="unpaidPreview.summary"
        :recipients="unpaidPreview.recipients"
        :no-phone="unpaidPreview.noPhone"
        @update:channel="onUnpaidChannelChange"
        @cancel="unpaidPreview.visible = false"
        @confirm="confirmSendUnpaidNotify"
    />
</template>

<script>
import axios from 'axios';
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import pagination from 'laravel-vue-pagination';
import * as XLSX from 'xlsx/xlsx.mjs';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import BarcodeScanner from '@/components/global/BarcodeScanner.vue';
import NotificationPreviewDialog from '@/components/notifications/NotificationPreviewDialog.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
    name: 'PaymentsPage',
    components: { Dropdown, Dialog, Textarea, pagination, Breadcrumb, BarcodeScanner, NotificationPreviewDialog },

    data() {
        // "الكل" is the default so the page loads every student up front
        // instead of only page 1 — fetchStudents() also sends show_all=1
        // whenever this option is selected, reusing
        // AdminReservationApiService::index()'s own "return every matching
        // row" flag instead of relying on per_page=10000 as a large-number
        // approximation.
        const per_pages = [
            { title: 'الكل', id: 10000 },
            { title: 10,     id: 10 },
            { title: 25,     id: 25 },
            { title: 50,     id: 50 },
            { title: 100,    id: 100 },
        ];
        return {
            // Students (paginated)
            reservations: { data: [] },
            currentPage: 1,
            paggination: '',
            per_page: per_pages[0],
            per_pages,

            // Filters
            word: '',
            gender: { id: 1, title: 'ولد' },
            gender_list: [
                { id: 1, title: 'ولد' },
                { id: 2, title: 'بنت' },
            ],

            // Financial data
            financialItems: [],
            loadingItems: true,
            selectedQrItem: null,
            // paymentsMap[student_id][item_id] = { id, paid_at, paid_by_name, notes }
            paymentsMap: {},

            // QR scan lookup
            scanLookupLoading: false,
            highlightedStudentId: null,

            // Payment details dialog
            detailsVisible: false,
            selectedPayment: null,

            // Notification dialog
            notifyVisible: false,
            paymentMessage: '',
            currentParentPhone: null,

            // Bulk unpaid notify
            bulkNotifyItem: null,
            bulkNotifying: false,

            // Unpaid bulk-notify preview/confirmation dialog
            unpaidPreview: {
                visible: false,
                loading: false,
                sending: false,
                whatsappAvailable: true,
                // Admin-selected sending channel — defaults to WhatsApp each
                // time the dialog is (re)opened; the admin can switch it, which
                // re-fetches the preview from the backend for that channel.
                channel: 'whatsapp',
                summary: {},
                recipients: [],
                noPhone: [],
            },

            // Templates cache
            templates: {},
        };
    },

    computed: {
        gradeStore() { return useGradeStore(); },

        currentGrade() {
            const slug = this.$route.params.gradeSlug ?? this.$route.params.slug;
            return this.gradeStore.activeGrades.find(g => g.slug === slug)
                ?? this.gradeStore.allGrades.find(g => g.slug === slug)
                ?? this.gradeStore.currentGrade;
        },

        gradeName() { return this.currentGrade?.name ?? ''; },

        perPageValue() { return this.per_page ? this.per_page.id : 10; },

        smsCount() { return Math.ceil(this.paymentMessage.length / 70) || 1; },

        extraRows() {
            return 27 - (Number(this.paggination) % 27);
        },

        breadcrumbs() {
            return [
                { name: 'الرئيسية', path: '/' },
                { name: this.gradeName || 'الفصل', path: '/' },
                { name: 'المدفوعات', path: this.$route.fullPath },
            ];
        },
    },

    methods: {
        // ── Data Loading ────────────────────────────────────────────────

        async fetchStudents(page = 1) {
            if (!this.currentGrade) return;
            this.currentPage = page;

            const formData = new FormData();
            formData.append('status', 1);
            formData.append('order_by_name', 1);
            formData.append('academic_year_id', this.currentGrade.id);
            if (this.word) formData.append('word', this.word);
            if (this.gender) formData.append('gender', this.gender.id);
            // "الكل" (per_pages[0]) — reuse the backend's own "return every
            // matching row" flag (AdminReservationApiService::index()) rather
            // than approximate it with a very large per_page.
            if (this.per_page?.id === 10000) formData.append('show_all', 1);

            axios.defaults.headers.common['per_page'] = this.perPageValue;

            axios.post(`/all_reservations?page=${page}`, formData)
                .then(res => {
                    this.reservations = res.data.data;
                    this.paggination = res.data.data.meta?.total ?? 0;
                })
                .catch(() => { this.reservations = { data: [] }; });
        },

        async loadFinancialItems() {
            this.loadingItems = true;
            if (!this.currentGrade) return;
            const res = await axios.get('/financial-items', {
                params: { academic_year_id: this.currentGrade.id },
            });
            this.financialItems = res.data.data ?? [];
            this.loadingItems = false;
        },

        async loadPayments() {
            if (!this.currentGrade) return;
            const res = await axios.get(`/payments/by-academic-year/${this.currentGrade.id}`);
            const data = res.data.data ?? [];
            const map = {};
            for (const row of data) {
                map[row.student_id] = {};
                for (const [itemId, details] of Object.entries(row.payment_details ?? {})) {
                    map[row.student_id][itemId] = details;
                }
            }
            this.paymentsMap = map;
        },

        // ── Payment State ────────────────────────────────────────────────

        isPaid(studentId, itemId) {
            return !!(this.paymentsMap[studentId]?.[String(itemId)]);
        },

        getPaidTitle(studentId, itemId) {
            const d = this.paymentsMap[studentId]?.[String(itemId)];
            if (!d) return '';
            const who = d.paid_by_name ? `\nبواسطة: ${d.paid_by_name}` : '';
            return `تم الدفع: ${this.formatDate(d.paid_at)}${who}`;
        },

        rowNumber(index) {
            return (this.currentPage - 1) * this.perPageValue + index + 1;
        },

        // ── Interactions ────────────────────────────────────────────────

        async handleCellClick(student, item) {
            if (this.isPaid(student.user_id, item.id)) {
                // Show details
                const d = this.paymentsMap[student.user_id][String(item.id)];
                this.selectedPayment = {
                    student_id: student.user_id,
                    student_name: student.name,
                    item_id: item.id,
                    item_name: item.name,
                    amount: item.amount,
                    paid_at: d.paid_at,
                    paid_by_name: d.paid_by_name,
                    notes: d.notes,
                };
                this.detailsVisible = true;
            } else {
                // Mark as paid
                await this.markPaid(student, item);
            }
        },

        async markPaid(student, item) {
            const res = await axios.post('/payments/mark-paid', {
                student_id: student.user_id,
                financial_item_id: item.id,
            }).catch(err => { console.error(err); return null; });

            if (!res?.data?.status) return;

            // Refresh payment map
            await this.loadPayments();

            // Payment Confirmation WhatsApp sending is disabled backend-side
            // (StudentPaymentController::sendPaymentConfirmation always returns
            // false now), so whatsapp_sent is never a real failure worth
            // surfacing here anymore.
        },

        async removePayment() {
            const result = await Swal.fire({
                title: 'إلغاء تسجيل الدفع',
                html: `سيتم إلغاء دفع <strong>${this.selectedPayment.item_name}</strong><br>للطالب: ${this.selectedPayment.student_name}`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'نعم، إلغاء الدفع',
                cancelButtonText: 'لا',
                confirmButtonColor: '#dc3545',
            });
            if (!result.isConfirmed) return;

            await axios.post('/payments/mark-paid', {
                student_id: this.selectedPayment.student_id,
                financial_item_id: this.selectedPayment.item_id,
            });
            this.detailsVisible = false;
            await this.loadPayments();
        },

        // ── Templates ────────────────────────────────────────────────

        async loadTemplates() {
            try {
                const res = await axios.get('/whatsapp-templates');
                const list = res.data.data ?? [];
                this.templates = Object.fromEntries(list.map(t => [t.code, t]));
            } catch { /* non-fatal */ }
        },

        // ── Notification ────────────────────────────────────────────────

        buildMessage(studentName, item, paidAt) {
            const tpl = this.templates['payment_confirmation'];
            if (tpl?.is_active) {
                return tpl.message
                    .replace(/\{\{student_name\}\}/g, studentName)
                    .replace(/\{\{fee_name\}\}/g, item.name)
                    .replace(/\{\{fee_amount\}\}/g, item.amount ?? '')
                    .replace(/\{\{date\}\}/g, this.formatDate(paidAt))
                    .replace(/\{\{time\}\}/g, this.formatTime(paidAt))
                    .replace(/\{\{teacher_name\}\}/g, 'أ.طارق المحلاوي');
            }
            const amountLine = item.amount ? `\nقيمة الرسوم:\n${item.amount} جنيه\n` : '';
            return `تم تسجيل سداد رسوم الطالب:\n${studentName}\n\nالبند المالي:\n${item.name}\n${amountLine}\nتاريخ السداد:\n${this.formatDate(paidAt)}\n\nالوقت:\n${this.formatTime(paidAt)}\n\nشكراً لكم.`;
        },

        // Params shared by BOTH the preview call and the actual send call.
        unpaidNotifyParams() {
            return {
                academic_year_id:  this.currentGrade.id,
                financial_item_id: this.bulkNotifyItem.id,
                word:              this.word || undefined,
                channel:           this.unpaidPreview.channel,
            };
        },

        // Loads/reloads the recipient preview for the currently-selected
        // channel — never sends anything itself. Shared by the initial open
        // and by switching WhatsApp/SMS inside the dialog.
        async loadUnpaidPreview() {
            this.unpaidPreview.loading = true;
            try {
                const { data } = await axios.post('/payments/notifications/preview', this.unpaidNotifyParams());
                this.unpaidPreview.summary = data.summary;
                this.unpaidPreview.recipients = data.recipients;
                this.unpaidPreview.noPhone = data.no_phone;
                this.unpaidPreview.whatsappAvailable = data.whatsapp_available;
            } catch {
                this.unpaidPreview.visible = false;
                Swal.fire({ icon: 'error', title: 'خطأ', text: 'تعذّر تحميل قائمة المستلمين', timer: 3000, showConfirmButton: false });
            } finally {
                this.unpaidPreview.loading = false;
            }
        },

        // Step 1: open the dialog and load the preview — never sends anything itself.
        async sendBulkUnpaidNotify() {
            if (!this.currentGrade || !this.bulkNotifyItem) return;

            this.bulkNotifying = true;
            this.unpaidPreview.visible = true;
            this.unpaidPreview.channel = 'whatsapp';
            await this.loadUnpaidPreview();
            this.bulkNotifying = false;
        },

        // Admin switched WhatsApp/SMS inside the dialog — the selected channel
        // is the actual sending method, so the preview is recomputed from the
        // backend for it (never guessed on the frontend).
        onUnpaidChannelChange(channel) {
            this.unpaidPreview.channel = channel;
            this.loadUnpaidPreview();
        },

        // Step 2: admin confirmed from the preview dialog — actual send.
        // Re-sends the SAME filters (never a list of student ids) so the
        // backend recomputes eligibility itself right before sending.
        async confirmSendUnpaidNotify() {
            this.unpaidPreview.sending = true;
            try {
                const res = await axios.post('/payments/bulk-notify-unpaid', this.unpaidNotifyParams());
                const { total_recipients, queued_chunks, failed_chunks, channel } = res.data;
                const channelLabel = channel === 'sms' ? 'SMS' : 'واتساب';
                this.unpaidPreview.visible = false;
                Swal.fire({
                    icon:  queued_chunks > 0 ? 'success' : 'info',
                    title: 'اكتمل الإرسال',
                    html:  `القناة: <strong>${channelLabel}</strong><br>عدد المستلمين: <strong>${total_recipients}</strong><br>دفعات تم إرسالها: <strong>${queued_chunks}</strong><br>دفعات فشلت: <strong>${failed_chunks}</strong>`,
                    timer: 6000,
                    showConfirmButton: true,
                });
            } catch (err) {
                const text = err?.response?.data?.message || 'حدث خطأ أثناء الإرسال';
                Swal.fire({ icon: 'error', title: 'خطأ', text, timer: 3000, showConfirmButton: false });
            } finally {
                this.unpaidPreview.sending = false;
            }
        },

        async sendNotification() {
            const phone = '2' + this.currentParentPhone;

            // 1. Check WhatsApp
            let hasWhatsApp = false;
            try {
                const r = await axios.post('/whatsapp/check', { phone });
                hasWhatsApp = r.data.has_whatsapp === true;
            } catch { hasWhatsApp = false; }

            // 2. Send WhatsApp
            if (hasWhatsApp) {
                try {
                    const r = await axios.post('/whatsapp/send', { phone, message: this.paymentMessage });
                    if (r.data.success) {
                        Swal.fire({ icon: 'success', title: 'تم الإرسال', text: 'تم إرسال الرسالة عبر واتساب بنجاح', timer: 1500, showConfirmButton: false });
                        this.notifyVisible = false;
                        return;
                    }
                } catch { /* fall through */ }
            }

            // 3. SMS fallback — same implementation as attendance
            const formData = new FormData();
            formData.append('SenderName', 'SMS invite');
            formData.append('RequestID', Date.now());
            formData.append('PhoneNumber', phone);
            formData.append('Message', this.paymentMessage);

            await axios.post(
                'https://hubapi.advansystelecom.com/api/bulkSMS/ForwardSMS',
                formData,
                { headers: { 'Authorization': '667d0a1e-f2b3-4bd5-8569-360b6e7b80b0', 'Content-Type': 'application/json' } }
            ).then(res => {
                if (res.data === '1') {
                    Swal.fire({ icon: 'success', title: 'تم الإرسال', text: 'تم إرسال الرسالة بنجاح', timer: 1500, showConfirmButton: false });
                    this.notifyVisible = false;
                }
            }).catch(err => {
                const code = err.response?.data?.Message;
                const msgs = { '-1': 'رمز التوثيق غير صحيح', '-2': 'رقم الجوال فارغ', '-3': 'نص الرسالة فارغ', '-4': 'اسم المرسل غير صحيح', '-5': 'لا يوجد رصيد كافٍ' };
                Swal.fire({ icon: 'error', title: 'خطأ', text: msgs[code] ?? 'حدث خطأ غير متوقع', timer: 3000, showConfirmButton: false });
            });
        },

        // ── Formatting ────────────────────────────────────────────────

        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
        },

        formatTime(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            let h = d.getHours();
            const m = String(d.getMinutes()).padStart(2, '0');
            const period = h >= 12 ? 'مساءً' : 'صباحاً';
            h = h % 12 || 12;
            return `${h}:${m} ${period}`;
        },

        // ── QR Payment ────────────────────────────────────────────────

        // Looks up students matching a scanned value (student id, reservation id, or
        // reservation code) within the current grade, via the same search endpoint
        // fetchStudents() uses.
        async searchStudentsForScan(term) {
            const formData = new FormData();
            formData.append('status', 1);
            formData.append('academic_year_id', this.currentGrade.id);
            formData.append('word', term);
            axios.defaults.headers.common['per_page'] = 10000;

            const res = await axios.post('/all_reservations', formData);
            return res.data.data?.data ?? [];
        },

        async handlePaymentQR({ raw, studentId }) {
            console.log('[Payments] Callback fired', { raw, studentId });

            const term = studentId ?? raw;
            if (!term || !this.selectedQrItem || !this.currentGrade) return;

            console.log('[Payments] Lookup started for:', term);
            this.scanLookupLoading = true;
            try {
                const matches = await this.searchStudentsForScan(term);

                if (!matches.length) {
                    console.log('[Payments] Lookup failed: no student found for', term);
                    Swal.fire({ icon: 'info', title: 'لم يتم العثور على الطالب', timer: 2500, showConfirmButton: false });
                    return;
                }

                let student;
                if (matches.length === 1) {
                    student = matches[0];
                } else {
                    console.log('[Payments] Lookup ambiguous, multiple matches:', matches.length);
                    student = await this.chooseFromMultipleMatches(matches);
                    if (!student) return; // user cancelled
                }

                console.log('[Payments] Lookup success:', student);
                await this.markPaidFromScan(student);
            } catch (err) {
                console.log('[Payments] Lookup failed:', err);
                Swal.fire({ icon: 'error', title: 'خطأ', text: 'تعذّر البحث عن الطالب', timer: 2500, showConfirmButton: false });
            } finally {
                this.scanLookupLoading = false;
            }
        },

        async chooseFromMultipleMatches(matches) {
            const inputOptions = Object.fromEntries(
                matches.map((s) => [s.user_id, `${s.name}${s.parent_phone ? ' - ' + s.parent_phone : ''}`])
            );
            const { value: chosenId } = await Swal.fire({
                icon: 'question',
                title: 'تم العثور على أكثر من طالب',
                input: 'radio',
                inputOptions,
                confirmButtonText: 'اختيار',
                cancelButtonText: 'إلغاء',
                showCancelButton: true,
            });
            if (!chosenId) return null;
            return matches.find((s) => String(s.user_id) === String(chosenId)) ?? null;
        },

        // Marks the resolved student paid for the selected financial item, then
        // scrolls to and highlights their row in the table.
        async markPaidFromScan(student) {
            const res = await axios.post('/payments/qr-mark-paid', {
                student_id: student.user_id,
                financial_item_id: this.selectedQrItem.id,
            });
            console.log('[Payments] Page action executed: payment marked for', student.user_id);

            const toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 4500,
                timerProgressBar: true,
            });

            if (res.data.status === 'already_paid') {
                toast.fire({
                    icon: 'warning',
                    title: 'تم تسجيل الدفع مسبقاً',
                    html: `<strong>${res.data.student_name}</strong><br>${res.data.item_name}`,
                });
            } else {
                toast.fire({
                    icon: 'success',
                    title: 'تم تسجيل الدفع',
                    html: `<strong>${res.data.student_name}</strong><br>${res.data.item_name}`,
                });
                await this.loadPayments();

                // Payment Confirmation WhatsApp sending is disabled backend-side
                // (StudentPaymentController::sendPaymentConfirmation always
                // returns false now), so whatsapp_sent is never a real failure
                // worth surfacing here anymore.
            }

            await this.revealStudentRow(student.user_id);
        },

        // Ensures the given student's row is actually rendered (clears filters/paging
        // that might otherwise hide it), then flashes a highlight in place.
        // NEVER calls scrollIntoView()/scrollTo() here — a QR scan must never move
        // the viewport.
        async revealStudentRow(studentId) {
            const alreadyVisible = this.reservations.data?.some((s) => s.user_id === studentId);
            if (!alreadyVisible) {
                this.word = '';
                this.gender = null;
                this.per_page = this.per_pages[0]; // "الكل"
                await this.fetchStudents(1);
            }

            await this.$nextTick();

            this.highlightedStudentId = studentId;
            setTimeout(() => {
                if (this.highlightedStudentId === studentId) this.highlightedStudentId = null;
            }, 2000);
        },

        // ── Export ────────────────────────────────────────────────

        exportToExcel() {
            const table = document.getElementById('paymentsTable').cloneNode(true);
            const ws = XLSX.utils.table_to_sheet(table);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'المدفوعات');
            XLSX.writeFile(wb, `مدفوعات-${this.gradeName}.xlsx`);
        },
    },

    async mounted() {
        if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
        if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();

        await Promise.all([this.loadFinancialItems(), this.loadPayments(), this.loadTemplates()]);
        this.fetchStudents(1);

        // Restore QR selected item from localStorage
        const saved = localStorage.getItem('selected_payment_item');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const match = this.financialItems.find(i => i.id === parsed.id);
                if (match) this.selectedQrItem = match;
            } catch {}
        }
    },

    watch: {
        '$route.params.gradeSlug': {
            async handler() {
                await Promise.all([this.loadFinancialItems(), this.loadPayments()]);
                this.fetchStudents(1);
                // Re-resolve selected QR item after reloading financial items
                const saved = localStorage.getItem('selected_payment_item');
                if (saved) {
                    try {
                        const parsed = JSON.parse(saved);
                        const match = this.financialItems.find(i => i.id === parsed.id);
                        this.selectedQrItem = match ?? null;
                    } catch {}
                }
            },
        },

        selectedQrItem(val) {
            if (val) {
                localStorage.setItem('selected_payment_item', JSON.stringify({ id: val.id, name: val.name }));
            } else {
                localStorage.removeItem('selected_payment_item');
            }
        },
    },
};
</script>

<style scoped>
/* ── Mobile: horizontal scroll instead of squeezing columns ──
   Desktop layout/columns are untouched (no rule here applies above this
   breakpoint). #paymentsTable itself becomes the scroll container so a
   swipe reveals the remaining columns, while the page/body never gains its
   own horizontal scrollbar — same breakpoint as the project's shared
   tablet stylesheet (_tablet.scss), scoped to this page only so it doesn't
   affect absence.vue's own use of the (untouched) .absence-page family. */
@media (max-width: 991.98px) {
    #paymentsTable {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        max-width: 100%;
    }
    #paymentsTable table.table-confirm {
        width: max-content;
        min-width: 100%;
    }
}

/* ── Payment cells ── */
.pay-cell {
    cursor: pointer;
    text-align: center;
    vertical-align: middle;
    min-width: 55px;
    transition: background-color 0.15s;
}
.pay-cell:hover {
    background-color: #e8f5e9 !important;
}
.pay-cell--paid {
    background-color: #d4edda;
}
.pay-cell--paid:hover {
    background-color: #c3e6cb !important;
}
.pay-check {
    color: #28a745;
    font-size: 1rem;
}

/* ── QR scan result ── */
.row-highlight {
    animation: row-highlight-flash 2s ease-out;
}
@keyframes row-highlight-flash {
    0%, 40%   { background-color: #fff3cd; }
    100%      { background-color: transparent; }
}

/* ── Column headers ── */
th.pay-col {
    min-width: 60px;
    font-size: 0.82rem;
    text-align: center;
    vertical-align: bottom;
    white-space: nowrap;
}
.item-amount {
    font-size: 0.7rem;
    color: #6c757d;
    font-weight: normal;
}

/* Sticky header: this table's two header rows (rowspan'd id/name/etc. columns
   + the per-financial-item month row) stick together as one unit — sticky on
   <thead> itself, not per-row/th, so no fragile row-height math is needed to
   stack a second row beneath the first. Pinned below the app's own sticky
   navbar (--navbar-height) so it never overlaps it. The page's existing
   horizontal scroll is untouched — sticky here only affects the vertical axis. */
#paymentsTable table.table-confirm thead {
    position: sticky;
    top: var(--navbar-height, 64px);
    z-index: 2;
}
#paymentsTable table.table-confirm thead th {
    background-color: var(--surface-card, #fff);
}
#paymentsTable table.table-confirm thead tr:last-child th {
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
}

/* ── Print styles ── */
/* Financial table is wide (id/name/parent phone + N month/item columns) —
   landscape roughly doubles the usable width vs. the browser's portrait
   default. */
@page {
    size: A4 landscape;
    margin: 10mm;
}

@media print {
    /* A blank-looking print/PDF is often caused by an unrelated page's
       global (unscoped) print rule — e.g. some page's own
       `body * { visibility: hidden !important; }` scoped only to reveal ITS
       own print target — leaking in here, because vue3-print-nb (see the
       v-print directive above) builds its print document by copying every
       rule out of document.styleSheets, not just this component's own CSS.
       Guarded against defensively, same fix already applied for this exact
       reason in this project's attendance.vue. */
    #paymentsTable,
    #paymentsTable * {
        visibility: visible !important;
    }

    html, body {
        width: 100% !important;
        overflow: visible !important;
    }

    /* THE actual cause of "text is almost invisible": this app's body is
       unconditionally dark-themed — style.scss's `body { color: #F0EDE4
       !important; background-color: #0D0D0D !important; }` — and
       design-system.css's `.table thead tr th { color: var(--text-secondary)
       !important; }` (which resolves to a near-white
       rgba(240,237,228,.62) — --text-primary/--text-secondary are defined on
       bare :root, so they still resolve even though the print iframe never
       gets the page's data-theme attribute). Both rules ship with
       !important and are copied into the print document verbatim, so they
       land on near-white/light print backgrounds as near-invisible text.
       Only an ID-scoped selector reliably outranks them. */
    #paymentsTable,
    #paymentsTable * {
        color: #000 !important;
    }
    body {
        background: #fff !important;
    }

    /* style.scss's shared .a4/.first-screen-pdf pair is a cover-page pattern
       built for dedicated print-report routes where a full A4 title page in
       front of the content is the point; reused as-is it forces
       #paymentsTable (via .a4) to a fixed one-page height and a 196mm-wide
       flex box just to show three lines of text. Compacted here into a
       header that flows with the table instead of reserving its own page —
       scoped to #paymentsTable only, style.scss itself untouched. */
    #paymentsTable {
        height: auto !important;
        min-height: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        margin: 0 !important;
        overflow: visible !important;
        direction: rtl;
    }
    /* table-layout:auto (the default, used on screen) only ever SHRINKS a
       column down to its content's minimum width — it never forces the
       table itself to fit a target width, so with several rowspan id/name/
       phone columns plus one th per financial item the table could still
       render wider than the printable page, and Chrome simply clips
       everything past the edge rather than scaling or wrapping to a new
       page (this, not scaling, is why whole columns went missing).
       table-layout:fixed makes width:100% a hard cap the browser cannot
       exceed: content wraps/shrinks inside each cell instead of growing
       the table. */
    #paymentsTable table {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        table-layout: fixed !important;
    }
    /* Explicit shares only for the 3 rowspan id columns (row 1) — every
       month/financial-item column (row 2, one th per item, un-widthed) then
       auto-divides the REMAINING page width equally between them. That
       division is what guarantees sum(column widths) is always <= 100%,
       however many financial items are shown. */
    #paymentsTable table.table-confirm thead tr:first-child th:nth-child(1) { width: 5%; }
    #paymentsTable table.table-confirm thead tr:first-child th:nth-child(2) { width: 18%; }
    #paymentsTable table.table-confirm thead tr:first-child th:nth-child(3) { width: 13%; }
    #paymentsTable table th,
    #paymentsTable table td {
        white-space: normal;
        word-break: break-word;
        overflow-wrap: break-word;
    }
    #paymentsTable .first-screen-pdf {
        display: block !important;
        width: auto !important;
        height: auto !important;
        border: none !important;
        margin: 0 0 4mm !important;
        page-break-after: avoid;
        break-after: avoid;
    }
    #paymentsTable .first-screen-pdf h1 {
        font-size: 1.4rem !important;
        margin: 0 0 2mm !important;
    }
    #paymentsTable .first-screen-pdf h2,
    #paymentsTable .first-screen-pdf h3 {
        font-size: 1rem !important;
        margin: 0 0 1mm !important;
    }

    #paymentsTable table.table-confirm thead {
        position: static !important;
        display: table-header-group;
    }
    #paymentsTable table.table-confirm tbody {
        display: table-row-group;
    }
    #paymentsTable table.table-confirm tbody tr {
        break-inside: avoid;
        page-break-inside: avoid;
    }

    /* style.scss also ships a leftover, unscoped `table thead/tbody
       th/td:nth-child(6),(8) { display:none }` print rule (a vestige of an
       older, wider table). Its bare `table` selector matches this table
       too: with 2+ financial items the 2nd/4th item column lands on
       position 6/8 and silently disappears from print. Neutralized here for
       #paymentsTable only; style.scss itself is left alone so any other
       page still relying on it is unaffected. */
    #paymentsTable table thead tr th,
    #paymentsTable table tbody tr td {
        display: table-cell !important;
    }

    .table > :not(caption) > * > * {
        border-width: 2px !important;
        border-color: #000 !important;
    }
    table * {
        font-size: 0.8rem !important;
        font-weight: 900;
    }
    /* No explicit width here on purpose — left un-widthed so table-layout:
       fixed auto-divides the remaining page width equally across however
       many month/item columns exist (see #paymentsTable table above). A
       fixed pixel width here would fight that and could push the table
       wider than the page again with many financial items. */
    th.pay-col {
        min-width: 0 !important;
        width: auto;
        white-space: normal;
    }
    .pay-cell--paid {
        background-color: #d4edda !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .pay-check {
        color: #1e7e34 !important;
    }
    .empty-row {
        display: table-row !important;
    }
}

.empty-row {
    display: none;
}
</style>
